import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Stack,
  useTheme,
  alpha,
  Fade
} from '@mui/material';
import {
  BarChart as ChartIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
import styled from 'styled-components';
import { apiCall } from "../../services/ApiCall.js";
import config from "../../config/config.json";

// Styled Components with #0d6efd theme
const StyledContainer = styled(Card)`
  background: rgba(255, 255, 255, 0.95) !important;
  border-radius: 20px !important;
  box-shadow: 0 2px 3.5px rgba(0, 0, 0, 0.15) !important;  /* fixed shadow */
  border: 1px solid rgba(13, 110, 253, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  overflow: hidden;
  position: relative;

 
`;


const CategoryChip = styled(Chip)`
  border-radius: 6px !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  cursor: pointer !important;
  
  &.active {
    background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%) !important;
    color: white !important;
    transform: scale(1.05);
    box-shadow: 0 4px 15px rgba(13, 110, 253, 0.3) !important;
  }
  
  &.inactive {
    background: rgba(13, 110, 253, 0.08) !important;
    color: rgba(13, 110, 253, 0.7) !important;
    
    &:hover {
      background: rgba(13, 110, 253, 0.15) !important;
      transform: translateY(-2px);
    }
  }
`;

const StyledSelect = styled(Select)`
  .MuiOutlinedInput-root {
    border-radius: 12px !important;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(13, 110, 253, 0.2);
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      border-color: #0d6efd;
    }
    
    &.Mui-focused {
      border-color: #0d6efd;
      box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
    }
  }
`;

const ChartContainer = styled(Box)`
  background: rgba(248, 250, 255, 0.5);
  border-radius: 16px;
  padding: 20px;
  border: 1px solid rgba(13, 110, 253, 0.08);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 0%, rgba(13, 110, 253, 0.03), transparent 50%);
    border-radius: 16px;
    pointer-events: none;
  }
`;

const RequestStatusChart = () => {
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = useState('New Request');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [requestData, setRequestData] = useState({
    'New Request': Array(12).fill(0),
    'In-Progress Request': Array(12).fill(0),
    'To-Be-Approved Request': Array(12).fill(0),
    'Approved Request': Array(12).fill(0),
  });

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, index) => currentYear - index);
  };

  const fetchData = async (year) => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getRequestGraphData`,
      {},
      {},
      "GET"
    );

    if (isSuccess) {
      const requestCounts = {
        'New Request': Array(12).fill(0),
        'In-Progress Request': Array(12).fill(0),
        'To-Be-Approved Request': Array(12).fill(0),
        'Approved Request': Array(12).fill(0),
      };

      const startYear = year;

      data?.data.forEach((request) => {
        const { status, updatedAt } = request;
        const requestDate = new Date(updatedAt);
        const requestYear = requestDate.getFullYear();
        const requestMonth = requestDate.getMonth();

        if (requestYear.toString() == startYear) {
          switch (status) {
            case '0':
              requestCounts['New Request'][requestMonth] += 1;
              break;
            case '1':
              requestCounts['In-Progress Request'][requestMonth] += 1;
              break;
            case '4':
              requestCounts['Approved Request'][requestMonth] += 1;
              break;
            case '2':
              requestCounts['To-Be-Approved Request'][requestMonth] += 1;
              break;
            default:
              break;
          }
        }
      });

      setRequestData(requestCounts);
    }
  };

  useEffect(() => {
    fetchData(selectedYear);
  }, [selectedYear]);

  // Enhanced chart options with modern styling
  const getChartColor = (category) => {
    const colors = {
      'New Request': '#0d6efd',
      'In-Progress Request': '#ffa726',
      'To-Be-Approved Request': '#f44336',
      'Approved Request': '#4caf50'
    };
    return colors[category] || '#0d6efd';
  };

  const options = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        }
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        horizontal: false,
        columnWidth: '60%',
        borderRadiusApplication: 'end'
      },
    },
    xaxis: {
      categories: [
        'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
        'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
      ],
      labels: {
        style: {
          colors: '#64748b',
          fontSize: '12px',
          fontWeight: 500
        }
      },
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: '#64748b',
          fontSize: '12px',
          fontWeight: 500
        }
      }
    },
    grid: {
      show: true,
      borderColor: alpha('#0d6efd', 0.1),
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false
        }
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.5,
        gradientToColors: [alpha(getChartColor(selectedCategory), 0.3)],
        inverseColors: false,
        opacityFrom: 0.9,
        opacityTo: 0.4,
        stops: [0, 100]
      }
    },
    colors: [getChartColor(selectedCategory)],
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      theme: 'light',
      style: {
        fontSize: '12px'
      },
      y: {
        formatter: function (value) {
          return value + ' requests';
        }
      }
    }
  };

  const series = [
    {
      name: selectedCategory,
      data: requestData[selectedCategory],
    },
  ];

  const categories = [
    { key: 'New Request', label: 'New Request', color: '#0d6efd' },
    { key: 'In-Progress Request', label: 'In-Progress', color: '#ffa726' },
    { key: 'To-Be-Approved Request', label: 'To-Be-Approved', color: '#f44336' },
    { key: 'Approved Request', label: 'Approved', color: '#4caf50' }
  ];

  const totalRequests = requestData[selectedCategory].reduce((sum, count) => sum + count, 0);

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: '#333333',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'left',
            mb: 1,
          }}
        >
          Analytics Dashboard
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            color: '#64748b',
            textAlign: 'left'
          }}
        >
          Companies & Sites Overview
        </Typography>
      </Box>


      <StyledContainer>
        <CardContent sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>


            {/* Category Selector */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {categories.map((category) => (
                  <CategoryChip
                    key={category.key}
                    label={category.label}
                    onClick={() => setSelectedCategory(category.key)}
                    className={selectedCategory === category.key ? 'active' : 'inactive'}
                    sx={{
                      mb: 1,
                      '&.inactive': {
                        borderColor: category.color,
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>


              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <StyledSelect
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {generateYearOptions().map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
              </Box>
            </Box>
          </Box>

          {/* Stats Summary */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: getChartColor(selectedCategory),
                  mr: 2
                }}
              >
                {totalRequests.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
                {selectedCategory} this year
              </Typography>

            </Box>

            <Typography
              variant="h7"
              sx={{
                fontWeight: 700,
                mt: 1,
                background: "linear-gradient(135deg, #3C82F6 0%, #234C90 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent"
              }}
            >
              {`${Math.max(...requestData[selectedCategory])} peak month`}
            </Typography>


          </Box>

          {/* Chart */}
          <ChartContainer>
            <Chart options={options} series={series} type="bar" height={250} />
          </ChartContainer>
        </CardContent>
      </StyledContainer>
    </>


  );
};

export default RequestStatusChart;